<template>
  <div v-if="shouldShow" class="fixed right-4 z-50 lg:hidden flex flex-col items-end sticky-phone-widget">
    <ContactForm :open="openContactForm" @hide-modal="hideContactForm" />

    <!-- "Need help?" Label Box - shown when closed -->
    <div v-if="!isExpanded" class="mb-2 w-full flex flex-col items-end">
      <div class="help-label-box relative flex items-center bg-white shadow-sm ml-auto">
        <span class="help-label-text">Need help?</span>
        <!-- Arrow pointing down -->
        <div class="help-label-arrow"></div>
      </div>
    </div>

    <!-- Expanded Content -->
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-4 scale-95"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-4 scale-95">
      <div v-if="isExpanded" class="mb-2 flex flex-col items-end gap-2">
        <!-- Phone Number Button -->
        <a
          :href="`tel:${phoneNumber}`"
          class="action-box flex items-center justify-center bg-white shadow-sm transition-all"
          @click.stop>
          <PhoneInTalkIcon class="action-icon text-[#385898] flex-shrink-0" />
          <span class="action-text whitespace-nowrap">{{ formattedPhoneNumber }}</span>
        </a>

        <!-- Email Contact Button -->
        <button
          type="button"
          class="action-box flex items-center justify-center bg-white shadow-sm transition-all"
          @click.stop="openContactForm = true">
          <MailIcon class="action-icon text-[#385898] flex-shrink-0" />
          <span class="action-text">E-mail us</span>
        </button>

        <!-- FAQ Button -->
        <RouterLink
          to="/faq"
          class="action-box flex items-center justify-center bg-white shadow-sm transition-all"
          @click.stop>
          <SupportAgentIcon class="action-icon text-[#385898] flex-shrink-0" />
          <span class="action-text">FAQ page</span>
        </RouterLink>
      </div>
    </Transition>

    <!-- Main Sticky Button -->
    <button
      type="button"
      class="sticky-button flex items-center justify-center focus:outline-none transition-all ml-auto"
      :aria-label="isExpanded ? 'Close help menu' : 'Need help?'"
      @click="toggleExpanded">
      <Transition
        enter-active-class="transition-opacity duration-200"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-200"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
        mode="out-in">
        <XMarkIcon v-if="isExpanded" key="close" class="button-icon text-white" />
        <PermPhoneMsgIcon v-else key="phone" class="button-icon text-white" />
      </Transition>
    </button>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, RouterLink } from "vue-router";
import { XMarkIcon } from "@heroicons/vue/24/outline";
import PermPhoneMsgIcon from "~icons/material-symbols/perm-phone-msg";
import PhoneInTalkIcon from "~icons/material-symbols/phone-in-talk";
import MailIcon from "~icons/material-symbols/mail";
import SupportAgentIcon from "~icons/material-symbols/support-agent";
import { isSportTravel } from "@/brandconfig";
import ContactForm from "@/components/ContactForm.vue";

const route = useRoute();
const isExpanded = ref(false);
const isMobile = ref(false);
const openContactForm = ref(false);

const phoneNumber = "+442046381450";
const formattedPhoneNumber = "+44 20 4638 1450";

const hideContactForm = () => {
  openContactForm.value = false;
  // Close the expanded menu when contact form closes
  isExpanded.value = false;
};

// Check if we're on mobile
const checkMobile = () => {
  isMobile.value = window.innerWidth < 1024; // lg breakpoint
};

// Check if we should show the widget
const shouldShow = computed(() => {
  if (!isSportTravel || !isMobile.value) {
    return false;
  }

  // Exclude checkout pages
  const path = route.path.toLowerCase();
  return !path.includes("/checkout") && !path.includes("/paymentpending");
});

// Toggle expanded state
const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
  // Close contact form if opening the menu
  if (isExpanded.value) {
    openContactForm.value = false;
  }
};

// Close when clicking outside
const handleClickOutside = (event) => {
  if (shouldShow.value && isExpanded.value) {
    const widget = event.target.closest(".fixed.bottom-4.right-4, .fixed.bottom-4.right-0");
    if (!widget) {
      isExpanded.value = false;
    }
  }
};

// Close when route changes
const closeOnRouteChange = () => {
  isExpanded.value = false;
};

onMounted(() => {
  checkMobile();
  window.addEventListener("resize", checkMobile);
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  window.removeEventListener("resize", checkMobile);
  document.removeEventListener("click", handleClickOutside);
});

// Watch route changes
watch(
  () => route.path,
  () => {
    closeOnRouteChange();
  }
);
</script>

<style scoped>
.sticky-button {
  width: 64px;
  height: 64px;
  background: #385898;
  border: 4.57143px solid #ffffff;
  box-shadow:
    0px 1.14286px 3.42857px rgba(0, 0, 0, 0.1),
    0px 1.14286px 2.28571px -1.14286px rgba(0, 0, 0, 0.1);
  border-radius: 1141.71px;
  padding: 13.7143px;
  box-sizing: border-box;
}

.button-icon {
  width: 36.57px;
  height: 36.57px;
}

.shadow-sm {
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.08);
}

.help-label-box {
  padding: 8px 12px;
  border-radius: 4px;
}

.help-label-text {
  font-family: "Poppins", sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 150%;
  color: #2b4882;
}

.help-label-arrow {
  position: absolute;
  width: 8px;
  height: 8px;
  left: 70%;
  bottom: -4px;
  background: #ffffff;
  border-radius: 2px;
  transform: translateX(-50%) rotate(45deg);
}

.action-box {
  padding: 8px 12px;
  gap: 8px;
  border-radius: 64px;
}

.action-icon {
  width: 24px;
  height: 24px;
}

.action-text {
  font-family: "Poppins", sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 150%;
  color: #2b4882;
}
</style>
